<template>
  <div>
    <div class="level mgt-20px">
      <div class="level-left">
        <div class="columns">
          <div class="column is-narrow">
            <form @submit.prevent="recherche()">
              <b-input
                v-model="critereRecherche.auto"
                :placeholder="$t('detailFournisseurSupport.produit.actions.search')"
                type="search" icon="magnify">
              </b-input>
            </form>
          </div>
          <div class="column is-narrow">
            <button class="button" @click="setShowFiltre">
              <b-icon
                icon="filter-variant"
                size="is-small"
                type="is-default"/>
              <span class="is-size-7">
                {{$t('general.button.filtre')}}
                <label
                  class="has-text-weight-bold"
                  :class="nombreCritere > 0 ? '' : 'is-invisible'">
                  ({{ nombreCritere }})
                </label>
              </span>
            </button>
            <span @click="reinitialiserFiltre()"
              class="info-circle-right is-pulled-right is-cursor-pointer">
              <b-icon icon="close-circle" type="is-danger"/>
            </span>
          </div>
        </div>
      </div>
      <div class="columns level-right">
        <div class="column is-narrow">
          <exportExcelButton
            :perm="permCatalogue.exporter"
            :query="exportExcelQuery"/>
        </div>
      </div>
    </div>
    <v-server-table
      url="" ref="tableListeProduits"
      :columns="columns"
      :options="options"
      @loaded="finLoading"
      @loading="debutLoading">
      <template slot="afterTable">
        <table-items-in-view
          @input="$refs.tableListeProduits.setLimit($event)"/>
      </template>
      <div slot="reference" slot-scope="props">
        <router-link
          :to="{
            name: getLibelleAndRoutesForProduct(props.row).routeDetail,
            params: {id: props.row.id}
          }"
          :class="{'text-access-no-permission': !checkPermissions(permCatalogue.lecture)}"
          class="lien">
          {{ props.row.reference }}
        </router-link>
      </div>
      <div slot="type" slot-scope="props">
        {{ $t(getLibelleAndRoutesForProduct(props.row).libelle) }}
      </div>
      <div slot="societe" slot-scope="props">
        {{ getLibelleSociete(props.row.supportCommercial.societeId) }}
      </div>
      <div slot="actif" slot-scope="props">
        {{ $t(`general.${props.row.actif ? 'non' : 'oui'}`) }}
      </div>
      <div slot="agence" slot-scope="props">
        {{ getAgenceLibelle(props.row.agenceId) }}
      </div>
    </v-server-table>
    <div class="componentBrief" v-show="showFiltre">
      <filtre-produit-fournisseur-support
        ref="filtreProduitFournisseurSupport"
        @toggleFiltre="setShowFiltre"
        @recherche="makeRechercheFilter()">
      </filtre-produit-fournisseur-support>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import exportExcelButton from '@/components/general/button/exportExcelButton';
import calculNombreCritere from '@/services/general/calculNombreCritere';
import tagInputToArray from '@/services/general/filters/tagInputToArray';
import { columnsSplice } from '@/services/general/columns';
import { CATALOGUE } from '@/configuration/permissionsBodacConf';
import * as produitConf from '@/configuration/catalogue/produitConf';
import { SET_LOCK_MENU } from '@/store/modules/societe-mutation-type';
import { PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';
import {
  NO_PER_PAGE_VALUE,
  RESULTS_PER_PAGE,
  SORT_ICON, TEXTS,
} from '@/configuration/clientTableConf';
import TableItemsInView from '@/components/general/TableItemsInView';
import FiltreProduitFournisseurSupport from '@/components/achat/fournisseurSupport/detailFournisseurSupport/produitFournisseurSupport/filtre';

const ProduitFournisseurSupport = createNamespacedHelpers('produitFournisseurSupport');
const Societe = createNamespacedHelpers('societe');
const User = createNamespacedHelpers('user');
const Parametres = createNamespacedHelpers('parametres');
const Permissions = createNamespacedHelpers('permissions');

export default {
  name: 'FournisseurSupportProduit',
  components: {
    TableItemsInView,
    FiltreProduitFournisseurSupport,
    exportExcelButton,
  },
  data() {
    return {
      showFiltre: false,
      permCatalogue: CATALOGUE,
    };
  },
  computed: {
    ...ProduitFournisseurSupport.mapGetters({
      totalCount: 'totalCount',
      items: 'items',
      critereRecherche: 'criteresRecherche',
      lastPayload: 'lastPayload',
    }),
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
      lockMenu: 'lockMenu',
      agenceActive: 'agenceActive',
      societesInformations: 'societesInformations',
    }),
    ...User.mapGetters({
      user: 'user',
    }),
    ...Parametres.mapGetters({
      parametresInterne: 'parametresInterne',
    }),
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    nombreCritere() {
      const banCritereKey = ['auto', 'sansProduitMateriel'];
      const nbCritere = calculNombreCritere(this.critereRecherche, banCritereKey);
      return nbCritere > 9 ? '+9' : nbCritere;
    },
    columns() {
      const columns = ['reference', 'type', 'libelle', 'agence', 'societe', 'actif'];
      if (this.user.agences.length <= 1) {
        columnsSplice(columns, 'agence');
      }
      return columns;
    },
    options() {
      return {
        sortIcon: SORT_ICON,
        headings: {
          reference: this.$i18n.t('detailFournisseurSupport.produit.table.reference'),
          type: this.$i18n.t('detailFournisseurSupport.produit.table.type'),
          libelle: this.$i18n.t('detailFournisseurSupport.produit.table.label'),
          societe: this.$i18n.t('detailFournisseurSupport.produit.table.societe'),
          agence: this.$i18n.t('detailFournisseurSupport.produit.table.agence'),
          actif: this.$i18n.t('detailFournisseurSupport.produit.table.actif'),
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_PAGE,
        texts: TEXTS,
        sortable: ['reference', 'libelle', 'type', 'actif'],
        columnsClasses: {},
        requestFunction: ((data) => {
          if (this.filtrerProduitParAgence && this.critereRecherche.agencesId.length === 0) {
            this.critereRecherche.agencesId = this.agenceActive.map((agence) => agence.agenceId);
          }
          if (!data.query) data.query = cloneDeep(this.critereRecherche);
          data.query.categorie = tagInputToArray(this.critereRecherche.categorie, 'code');
          if (this.filtrerProduitParSociete) {
            data.query.societeId = this.societeActiveId;
          }
          if (this.user.agences.length === 1) {
            data.query.agencesId = [this.user.agences[0].agenceId];
          }
          data.query.fournisseurSupportIdFromMarche = this.$route.params.id;
          return this.actionLoad(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.items,
            count: this.totalCount,
          };
        }),
      };
    },
    filtrerProduitParSociete() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.filtrerProduitParSociete.key);
    },
    filtrerProduitParAgence() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.filtrerProduitParAgence.key);
    },
    exportExcelQuery() {
      return {
        typeDonnee: 'produits',
        typeExport: null,
        filtres: this.lastPayload,
      };
    },
  },
  methods: {
    ...ProduitFournisseurSupport.mapActions({
      actionLoad: 'actionLoad',
    }),
    ...Societe.mapMutations({
      setLockMenu: SET_LOCK_MENU,
    }),
    ...Societe.mapActions({
      loadSocietes: 'loadSocietes',
    }),
    ...Parametres.mapActions({
      loadParametresInterne: 'loadParametresInterne',
    }),
    finLoading() {
      this.loadingComponent.close();
    },
    debutLoading() {
      if (this.loadingComponent) {
        this.loadingComponent.close();
      }
      this.loadingComponent = this.$buefy.loading.open();
    },
    recherche() {
      this.$refs.tableListeProduits.refresh();
    },
    makeRechercheFilter() {
      if (this.showFiltre) this.showFiltre = !this.showFiltre;
      this.setLockMenu(!this.lockMenu);
      this.recherche();
    },
    async setShowFiltre() {
      this.showFiltre = !this.showFiltre;
      if (this.showFiltre) {
        await this.$refs.filtreProduitFournisseurSupport.initializeFiltre();
      }
      this.setLockMenu(!this.lockMenu);
    },
    async reinitialiserFiltre() {
      await this.$refs.filtreProduitFournisseurSupport.initializeFiltre();
      this.$refs.filtreProduitFournisseurSupport.viderCritere();
      this.$refs.filtreProduitFournisseurSupport.appliquerCritere();
      this.showFiltre = false;
    },
    getLibelleAndRoutesForProduct(produit) {
      const produitConfiguration = produitConf.CATEGORIE_PRODUIT.find(
        (produitObject) => {
          return produitObject.code === produit.supportCommercial.categorieVente;
        },
      );
      if (!produitConfiguration) return '';
      if (produitConfiguration.code !== produitConf.CATEGORIE_LOCATION) return produitConfiguration;
      return produit.numerique ? produitConfiguration.numerique : produitConfiguration.nonNumerique;
    },
    getLibelleSociete(societeId) {
      const societe = this.societesInformations.find((societe) => {
        return societe.id === societeId;
      });
      if (societe) return societe.libelle;
      return null;
    },
    getAgenceLibelle(agenceId) {
      let libelleAgence = '';
      this.societesInformations.forEach((societe) => {
        societe.agences.forEach((agence) => {
          if (agence.id === agenceId) libelleAgence = agence.libelle;
        });
      });
      return libelleAgence;
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadSocietes({});
      await this.loadParametresInterne([
        PARAMETRES_INTERNE_KEYS.filtrerProduitParSociete.key,
        PARAMETRES_INTERNE_KEYS.filtrerProduitParAgence.key,
      ]);
      if (this.user.agences.length > 1) {
        this.critereRecherche.agencesId = this.agenceActive.map((agence) => agence.agenceId);
      }
      if (this.societesInformations.length === 1 || this.filtrerProduitParSociete
        || this.filtrerProduitParAgence) {
        this.columns.splice(this.columns.findIndex(column => column === 'societe'), 1);
      }
      loadingComponent.close();
    } catch (e) {
      loadingComponent.close();
      throw e;
    }
  },
};
</script>
